<template>
  <div class="masterDetail panel">
    <div class="masterDetailHeader">
      <div>
        <div class="masterDetailTitle">{{ title }}</div>
        <div v-if="subtitle" class="masterDetailSubtitle">{{ subtitle }}</div>
      </div>
      <div class="masterDetailActions">
        <slot name="header-actions" />
      </div>
    </div>

    <div class="masterDetailBody" :style="bodyStyle">
      <section
        ref="masterPaneEl"
        class="masterPane"
        tabindex="0"
        @keydown.down.prevent="$emit('navigate', 'next')"
        @keydown.up.prevent="$emit('navigate', 'prev')"
        @keydown.home.prevent="$emit('navigate', 'first')"
        @keydown.end.prevent="$emit('navigate', 'last')"
      >
        <div v-if="items.length === 0" class="masterEmpty">
          <slot name="empty-list">No items.</slot>
        </div>
        <div v-else class="masterList" @scroll.passive="onMasterListScroll">
          <button
            v-for="item in items"
            :key="itemKey(item)"
            :ref="setRowEl(item)"
            type="button"
            class="masterRow"
            :class="{ selected: isSelected(item) }"
            @click="$emit('select', item)"
          >
            <slot name="row" :item="item" :selected="isSelected(item)" />
          </button>
          <slot name="after-list" />
        </div>
      </section>

      <section ref="detailPaneEl" class="detailPane">
        <slot name="detail" :selected-item="selectedItem">
          <div class="detailEmpty">Select an item.</div>
        </slot>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  items: { type: Array, default: () => [] },
  bodyMinHeight: { type: String, default: '420px' },
  bodyMaxHeight: { type: String, default: '' },
  detailOverflow: { type: String, default: 'auto' },
  selectedId: { type: [String, Number], default: '' },
  itemId: { type: Function, default: (item) => item?.id },
})

const emit = defineEmits(['select', 'navigate', 'load-more'])

const rowElsById = ref(new Map())
const masterPaneEl = ref(null)
const detailPaneEl = ref(null)

function setRowEl(item) {
  const key = itemKey(item)
  return (el) => {
    const map = rowElsById.value
    if (!map) return
    if (el) map.set(key, el)
    else map.delete(key)
  }
}

function itemKey(item) {
  return String(props.itemId(item) ?? '')
}

function isSelected(item) {
  return itemKey(item) === String(props.selectedId ?? '')
}

const selectedItem = computed(() => {
  return props.items.find((item) => isSelected(item)) || null
})

async function focusSelectedRow() {
  await nextTick()
  const key = String(props.selectedId ?? '')
  if (!key) return
  let rowEl = rowElsById.value?.get(key)
  if (!rowEl) {
    await nextTick()
    rowEl = rowElsById.value?.get(key)
  }
  if (!rowEl || typeof rowEl.focus !== 'function') return
  try {
    rowEl.focus({ preventScroll: true })
    rowEl.scrollIntoView({ block: 'nearest' })
  } catch {
    rowEl.focus({ preventScroll: true })
    rowEl.scrollIntoView({ block: 'nearest' })
  }
}

function onMasterListScroll(event) {
  const el = event.target
  if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
    emit('load-more')
  }
}

const bodyStyle = computed(() => {
  const style = { minHeight: props.bodyMinHeight }
  if (props.bodyMaxHeight) style.maxHeight = props.bodyMaxHeight
  return style
})

watch(() => String(props.selectedId ?? ''), async (nextId, prevId) => {
  if (!nextId || nextId === prevId) return
  // Only scroll/focus when the master pane (or one of its rows) already holds focus.
  // This prevents the component from stealing scroll position on initial data load.
  const active = document.activeElement
  if (active && active !== document.body && !(masterPaneEl.value instanceof Element && masterPaneEl.value.contains(active))) return
  await focusSelectedRow()
}, { flush: 'post' })

watch(() => props.items.length, async () => {
  if (!String(props.selectedId ?? '')) return
  // Only refocus when the master pane (or one of its rows) currently holds focus.
  // If focus is anywhere else — detail pane, a search input, etc. — leave it alone.
  const active = document.activeElement
  if (active && active !== document.body && !(masterPaneEl.value instanceof Element && masterPaneEl.value.contains(active))) return
  await focusSelectedRow()
}, { flush: 'post' })

onMounted(async () => {
  if (!String(props.selectedId ?? '')) return
  await focusSelectedRow()
})
</script>

<style scoped>
.masterDetail {
  padding: 0;
  overflow: hidden;
}

.masterDetailHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border-bottom: 1px solid var(--border);
}

.masterDetailTitle {
  font-weight: 700;
  color: var(--text);
}

.masterDetailSubtitle {
  margin-top: 4px;
  font-size: 12px;
  color: var(--muted);
}

.masterDetailActions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}

.masterDetailBody {
  display: grid;
  grid-template-columns: minmax(250px, 30%) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  min-height: 0;
  height: 100%;
}

.masterPane {
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border);
  min-width: 0;
  min-height: 0;
  height: 100%;
  outline: none;
  background: color-mix(in srgb, var(--surface) 92%, var(--badge-bg));
  overflow: hidden;
}

.masterPane:focus-visible {
  outline: 2px solid rgba(59, 130, 246, 0.4);
  outline-offset: -2px;
}

.masterList {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.masterRow {
  width: 100%;
  flex: 0 0 auto;
  min-height: 0;
  border: none;
  border-bottom: 1px solid var(--border2);
  background: transparent;
  text-align: left;
  padding: 12px 14px;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease;
}

.masterRow:focus {
  outline: none;
}

.masterRow:hover {
  background: var(--row-hover-bg);
}

.masterRow.selected {
  background: rgba(59, 130, 246, 0.08);
  box-shadow: inset 3px 0 0 #2563eb;
}

.masterRow:last-child {
  border-bottom: none;
}

.detailPane {
  min-width: 0;
  min-height: 0;
  height: 100%;
  background: var(--surface);
  overflow: v-bind('props.detailOverflow');
}

.masterEmpty,
.detailEmpty {
  padding: 14px;
  color: var(--muted);
}
</style>
